import {
  CustomButton,
  CustomCollapseCard,
  CustomImage,
  CustomPageHeader,
  CustomPagination,
  CustomTable,
  successDialog,
  Text,
  warningDialog,
  useText,
} from '@src/components';
import React, { useCallback, useEffect, useState } from 'react';
import '@src/styles/myOrders/deliveryConfirmationList.scss';
import OrderSummaryCardModal from '../OrderSummaryCardModal';
import iconInfo from '@src/assets/icons/icon-bilgi.svg';
import { Button } from 'antd';
import iconBack from '@src/assets/icons/icon-back.svg';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  orderInfosGetList,
  packagesGetPagedList,
  packagesUpdate,
} from '@src/store/slice/orderSlice';
import dayjs from '@src/utils/day';
import { defaultDateFormat } from '@src/utils/keys';
import { useLang } from '@src/context';

const DeliveryConfirmation = () => {
  const [orderSummaryCardModalVisible, setOrderSummaryCardModalVisible] = useState(false);
  const history = useHistory();

  const dispatch = useDispatch();
  const { selectedOrder } = useSelector((state) => state?.order);

  const [data, setData] = React.useState({
    list: [],
    tableProperty: {
      currentPage: 1,
      page: 1,
      pageSize: 10,
      totalCount: 0,
    },
  });

  const orderStatusText = useText('orderStatus');
  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const { dictionary, language } = useLang();

  const getPageList = useCallback(
    async (pageNumber, pageSize) => {
      const action = await dispatch(
        packagesGetPagedList({
          body: {
            isDeliveredToCustomer: false,
            orderId: selectedOrder?.id,
          },
          pageNumber,
          pageSize,
        }),
      );
      if (packagesGetPagedList.fulfilled.match(action)) {
        setData({
          list: action?.payload?.data?.items,
          tableProperty: action?.payload?.data?.pagedProperty,
        });
      }
    },
    [dispatch, selectedOrder],
  );

  useEffect(() => {
    if (selectedOrder?.id) {
      (async () => {
        await getPageList(1, 10);
      })();
    } else {
      history.push('/ticket-restaurant/my-orders');
    }
  }, [dispatch, getPageList, history, selectedOrder?.id, language]);

  const handleTableChange = useCallback(
    async (pageNumber, pageSize) => {
      await getPageList(pageNumber, pageSize);
    },
    [getPageList],
  );

  const handleDeliveryConfirm = useCallback(
    async (record) => {
      const action = await dispatch(packagesUpdate({ id: record?.id }));
      if (packagesUpdate.fulfilled.match(action)) {
        successDialog({
          title: <Text t="success" />,
          message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
          onOk: async () => {
            await getPageList(1, 10);
          },
        });
      }
    },
    [dispatch, getPageList],
  );

  const handleDetail = () => {
    setOrderSummaryCardModalVisible(true);
  };

  const handleInfo = async (record) => {
    const body = {
      orderId: record?.id,
      vouId: 1,
      orderType: record?.orderTypeId,
    };
    const action = await dispatch(orderInfosGetList(body));
    if (orderInfosGetList.fulfilled.match(action)) {
      const rawMessage = action?.payload?.data[0]?.message || '';
      warningDialog({
        title: <Text t={orderStatusText} />,
        message: dictionary[rawMessage] || rawMessage,
      });
    }
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      fixed: 'left',
      align: 'center',
      width: 37,
      render: (_, __, index) =>
        (data?.tableProperty?.currentPage - 1) * data?.tableProperty?.pageSize + index + 1,
    },
    {
      title: 'SİPARİŞ NO',
      dataIndex: 'orderId',
      key: 'orderId',
      align: 'left',
      width: 100,
      render: (text) => {
        return (
          <div className="cell-detail">
            {text}
            <span onClick={() => handleDetail()}>{` (detay)`}</span>
          </div>
        );
      },
    },
    {
      title: 'SİPARİŞ DURUMU',
      dataIndex: 'orderStatusName',
      key: 'orderStatusName',
      width: 210,
      align: 'left',
      render: (text, record) => {
        return (
          <span>
            {text}{' '}
            {record?.cargoLink !== '' && (
              <CustomImage
                style={{ cursor: 'pointer' }}
                src={iconInfo}
                onClick={() => handleInfo(record)}
              />
            )}
          </span>
        );
      },
    },
    {
      title: 'PAKET NUMARASI',
      dataIndex: 'id',
      key: 'id',
      width: 140,
    },
    {
      title: 'PAKET TARİHİ',
      dataIndex: 'packageDate',
      key: 'packageDate',
      width: 120,
      align: 'left',
      render: (text) => {
        return text ? dayjs(text).format(defaultDateFormat) : '-';
      },
    },
    {
      title: 'TESLİMAT ADRESİ',
      dataIndex: 'deliveryAddress',
      key: 'deliveryAddress',
      width: 140,
      align: 'left',
    },
    {
      title: 'ONAY DURUMU',
      dataIndex: 'orderStatusName',
      key: 'orderStatusName',
      width: 160,
      align: 'left',
    },
    {
      title: 'ONAY TARİHİ',
      dataIndex: 'approvalDate',
      key: 'approvalDate',
      width: 110,
      align: 'left',
      render: (text) => {
        return text ? dayjs(text).format(defaultDateFormat) : '-';
      },
    },
    {
      title: 'TESLİMAT ONAYI',
      width: 160,
      dataIndex: 'orderViewAction',
      key: 'orderViewAction',
      align: 'center',
      render: (_, record) => {
        return (
          <div className="table-action-content">
            <CustomButton
              id="ticketRestaurant-myOrders-deliveryConfirmation-main-delivery-btn"
              className="delivery-btn"
              onClick={() => handleDeliveryConfirm(record)}
            >
              <Text t="deliveryConfirmation2" />
            </CustomButton>
          </div>
        );
      },
    },
  ];

  return (
    <CustomPageHeader
      data-testid="delivery-confirmation"
      title={<Text t="deliveryConfirmation" />}
      showBreadCrumb
      showHelpButton
    >
      <CustomCollapseCard
        className="delivery-confirmation-list-card"
        cardTitle={<Text t="deliveryConfirmation" />}
      >
        <CustomTable
          pagination={false}
          dataSource={data?.list}
          columns={columns}
          className="delivery-confirmation-list-table"
          rowKey={(record) => `draft-list-${record?.id || record?.name}`}
        />

        <div className="delivery-confirmation-list-footer">
          <CustomPagination
            current={data?.tableProperty?.currentPage}
            defaultCurrent={1}
            pageSize={data?.tableProperty?.pageSize}
            total={data?.tableProperty?.totalCount}
            onChange={async (pageNumber, pageSize) => {
              await handleTableChange(pageNumber, pageSize);
            }}
          />
        </div>
      </CustomCollapseCard>

      <div className="delivery-confirmation-btn-container">
        <Button
          id="myOrders-deliveryConfirmation-main-back-btn"
          className="back-btn"
          onClick={() => history.goBack()}
        >
          <CustomImage src={iconBack} />
          <Text t="turnBack" />
        </Button>
      </div>

      <OrderSummaryCardModal
        modalVisible={orderSummaryCardModalVisible}
        handleModalVisible={setOrderSummaryCardModalVisible}
      />
    </CustomPageHeader>
  );
};

export default DeliveryConfirmation;
